/**
 * Email for system admins (design 3.9, step N3): send yourself a test message, and see what was
 * sent, what is retrying and what was given up. The transport is configured on the server.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { EmailDelivery } from "@workcore/schemas";
import { checkedWrite, get } from "../api";
import { wsKey } from "../directory";
import { EmptyState, ErrorBox, PageHeader, Panel, StatusBadge, When, toast } from "../ui";

const state = (d: EmailDelivery) => (d.sentAt ? "sent" : d.deadAt ? "given_up" : d.lastError ? "retrying" : "queued");
const KIND: Record<EmailDelivery["kind"], string> = { object: "Item", digest: "Daily summary", test: "Test" };

export function MailPage() {
  const { slug = "" } = useParams();
  const q = useQueryClient();
  const [failed, setFailed] = useState(false);
  const list = useQuery({
    queryKey: wsKey(slug, `mail-${failed ? "failed" : "all"}`),
    queryFn: async () => z.array(EmailDelivery).parse(await get(`/v1/ws/${slug}/mail/deliveries`, { limit: 100, ...(failed ? { failed: "true" } : {}) })),
    refetchInterval: 5_000,
  });
  const test = useMutation({
    mutationFn: () => checkedWrite<EmailDelivery>("POST", `/v1/ws/${slug}/mail/test`),
    onSuccess: (d) => {
      toast(`Test message queued for ${d.to}`);
      void q.invalidateQueries({ queryKey: ["ws", slug] });
    },
  });
  const rows = list.data ?? [];
  return (
    <>
      <PageHeader
        title="Email"
        description="Messages workcore sends for notifications. How mail is sent (SMTP or Microsoft 365) is set on the server, not here."
        actions={<button onClick={() => test.mutate()} disabled={test.isPending}>Send me a test message</button>}
      />
      <ErrorBox error={test.error ?? list.error} />
      <Panel>
        <div className="inline wrap toolbar">
          <label className="check"><input type="checkbox" checked={failed} onChange={(e) => setFailed(e.target.checked)} /> failures only</label>
        </div>
        {list.data && rows.length === 0 && (
          <EmptyState title={failed ? "No failures" : "No email yet"} body={failed ? "Nothing has failed to send." : "Messages appear here as they are made. Send yourself a test message to check mail works."} />
        )}
        {rows.length > 0 && (
          <table className="grid">
            <thead><tr><th>To</th><th>Subject</th><th>Kind</th><th>State</th><th>Attempts</th><th>When</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.to}</td>
                  <td>{d.subject}{d.lastError && <div className="muted small">{d.lastError}</div>}</td>
                  <td>{KIND[d.kind]}</td>
                  <td><StatusBadge status={state(d)} /></td>
                  <td>{d.attempts}</td>
                  <td><When at={d.sentAt ?? d.deadAt ?? d.createdAt} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
